import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, Phone, CheckCircle2, Info, BookOpen } from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";
import Breadcrumbs from "./Breadcrumbs";
import SEO from "./SEO";
import RelatedClaimHelp from "./RelatedClaimHelp";
import ReviewedBy from "./ReviewedBy";
import { guidePages } from "../data/guidePages";
import { businessInfo } from "../mock";
import { localBusinessSchema, faqSchema, breadcrumbSchema } from "../utils/schemas";

const Block = ({ block }) => {
  if (block.type === "h2") {
    return (
      <h2
        className="mt-10 text-2xl sm:text-3xl font-black text-white uppercase leading-tight"
        style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
      >
        {block.text}
      </h2>
    );
  }
  if (block.type === "p") {
    return <p className="mt-4 text-white/80 leading-relaxed text-base sm:text-lg">{block.text}</p>;
  }
  if (block.type === "list") {
    return (
      <ul className="mt-5 space-y-3">
        {block.items.map((it, i) => (
          <li key={i} className="flex items-start gap-3">
            <CheckCircle2 className="h-5 w-5 text-[#E11D26] shrink-0 mt-0.5" />
            <span className="text-white/80 text-base leading-relaxed">{it}</span>
          </li>
        ))}
      </ul>
    );
  }
  if (block.type === "callout") {
    return (
      <div className="mt-6 flex items-start gap-3 bg-[#E11D26]/10 border-l-4 border-[#E11D26] p-5">
        <Info className="h-5 w-5 text-[#E11D26] shrink-0 mt-0.5" />
        <p className="text-white/90 leading-relaxed text-base">{block.text}</p>
      </div>
    );
  }
  if (block.type === "table") {
    return (
      <div className="mt-7 overflow-x-auto border border-white/10">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="bg-[#E11D26]">
              {block.headers.map((h, i) => (
                <th key={i} className="px-4 py-3 text-white font-black uppercase tracking-wide text-xs">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.rows.map((row, ri) => (
              <tr key={ri} className={ri % 2 ? "bg-[#0F0F0F]" : "bg-black"}>
                {row.map((cell, ci) => (
                  <td
                    key={ci}
                    className={`px-4 py-3 align-top border-t border-white/10 ${
                      ci === 0 ? "text-white font-bold" : "text-white/75"
                    }`}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  return null;
};

const GuidePage = () => {
  const { slug } = useParams();
  const page = guidePages[slug];
  if (!page) return <Navigate to="/" replace />;

  const schemas = [
    localBusinessSchema(),
    faqSchema(page.faqs),
    breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Guides", path: "/#guides" },
      { name: page.h1, path: `/${page.slug}` },
    ]),
  ];

  return (
    <div className="bg-black min-h-screen text-white">
      <SEO
        title={page.metaTitle}
        description={page.metaDescription}
        path={`/${page.slug}`}
        keywords={page.keywords}
        schemas={schemas}
      />
      <Header />
      <Breadcrumbs items={[{ name: page.eyebrow, path: `/${page.slug}` }]} />

      {/* Hero */}
      <section className="bg-black border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14 md:py-20">
          <div className="flex items-center gap-2 mb-4">
            <BookOpen className="h-4 w-4 text-[#E11D26]" />
            <span className="text-[#E11D26] text-xs font-bold tracking-[0.25em] uppercase">
              {page.eyebrow}
            </span>
          </div>
          <h1
            className="text-white text-3xl sm:text-4xl md:text-5xl font-black uppercase leading-[1.02]"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            {page.h1}
          </h1>
          <p className="mt-6 text-white/80 text-base sm:text-lg leading-relaxed">
            {page.intro}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link
              to="/#contact"
              data-testid="guide-cta-review"
              className="group inline-flex items-center justify-center gap-3 bg-[#E11D26] hover:bg-[#c11820] text-white font-bold uppercase tracking-wider px-7 py-4 transition-colors"
            >
              <span className="text-sm">Get My Free Claim Review</span>
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Link>
            <a
              href={`tel:${businessInfo.phoneRaw}`}
              className="inline-flex items-center justify-center gap-2 bg-white text-black hover:bg-white/90 font-bold uppercase tracking-wider px-7 py-4 transition-colors"
            >
              <Phone className="h-4 w-4" />
              <span className="text-sm">{businessInfo.phone}</span>
            </a>
          </div>
        </div>
      </section>

      {/* Body */}
      <article className="py-14 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          {page.content.map((block, i) => (
            <Block key={i} block={block} />
          ))}
          <p className="mt-8 text-white/45 text-xs italic">
            This information is general and educational, not legal advice.
            Florida statutes and fee rules can change; review your specific fee
            agreement and consult the current Florida Statutes or an attorney
            for your situation.
          </p>
        </div>
      </article>

      {/* FAQ */}
      <section className="py-14 bg-black border-y border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2
            className="text-2xl sm:text-3xl font-black text-white uppercase"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            Frequently Asked Questions
          </h2>
          <div className="mt-8 divide-y divide-white/10 border-y border-white/10">
            {page.faqs.map((f, i) => (
              <details key={i} className="group py-5" data-testid={`guide-faq-${i}`}>
                <summary className="flex justify-between items-start cursor-pointer list-none gap-6">
                  <h3 className="text-white text-base sm:text-lg font-bold uppercase tracking-wide leading-snug">
                    {f.question}
                  </h3>
                  <span className="text-[#E11D26] text-xl group-open:rotate-45 transition-transform shrink-0">
                    +
                  </span>
                </summary>
                <p className="mt-4 text-white/75 leading-relaxed text-sm sm:text-base">
                  {f.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="py-14 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2
            className="text-2xl sm:text-3xl font-black text-white uppercase"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            Related Guides
          </h2>
          <div className="mt-6 grid sm:grid-cols-3 gap-3">
            {page.related.map((r) => (
              <Link
                key={r.href}
                to={r.href}
                className="group flex items-center justify-between gap-4 bg-[#0F0F0F] border border-white/10 hover:border-[#E11D26] hover:bg-[#1a0709] p-5 transition-colors"
              >
                <span className="text-white font-bold text-sm uppercase tracking-wide">
                  {r.text}
                </span>
                <ArrowRight className="h-4 w-4 text-[#E11D26] transition-transform group-hover:translate-x-1" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <ReviewedBy reviewer={page.reviewer || "Joe"} />

      <RelatedClaimHelp
        topic={
          page.slug === "how-much-does-a-public-adjuster-cost-in-florida"
            ? "fees"
            : "guide"
        }
        currentPath={`/${page.slug}`}
      />

      {/* Final CTA */}
      <section className="py-14 bg-[#E11D26]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <h3
            className="text-white text-2xl sm:text-3xl md:text-4xl font-black uppercase leading-tight"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            Still have questions? Get a free claim review.
          </h3>
          <a
            href={`tel:${businessInfo.phoneRaw}`}
            className="inline-flex items-center justify-center gap-2 bg-black hover:bg-[#0A0A0A] text-white font-bold uppercase tracking-wider px-7 py-4 transition-colors"
          >
            <Phone className="h-4 w-4" /> {businessInfo.phone}
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GuidePage;
